import { Chip } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"
import dayjs from "dayjs"

import { PriceChangeHistoryStatusCell } from "../components/PriceChangeHistoryStatusCell"

import { DATE_FORMAT } from "@/constants/formats"
import type { PriceChangeSubmission } from "@/graphql/codegen/graphql"
import PriceChangeHistoryRowAction from "@/screens/Pricing/components/tables/PriceChangeHistoryRowActions"
import type { RecipientType } from "@/screens/Pricing/constants"

interface UsePriceChangeHistoryColumnsProps {
  onRevert?: (submissionId: string) => void
  isPurchasingCost?: boolean
  onResendToAllCustomers?: (submission: PriceChangeSubmission) => void
  onResendToIndividual?: (submission: PriceChangeSubmission, recipientType: RecipientType) => void
}

export const usePriceChangeHistoryColumns = ({
  onRevert,
  isPurchasingCost,
  onResendToAllCustomers,
  onResendToIndividual,
}: UsePriceChangeHistoryColumnsProps): ColumnDef<PriceChangeSubmission>[] => {
  return [
    {
      accessorKey: "changeId",
      header: "Change ID",
      cell: ({ row }) => row.original.changeId,
      minSize: 120,
      enableSorting: false,
    },
    {
      accessorKey: "updatedOn",
      header: "Date Changed",
      cell: ({ row }) => (
        <Chip
          label={row.original.dateChanged ? dayjs(row.original.dateChanged).format(DATE_FORMAT.US) : ""}
          size='small'
          className='rounded-sm bg-primary-200'
          data-testid='price-change-history.updated-on-chip'
        />
      ),
      minSize: 140,
      enableSorting: true,
    },
    {
      accessorKey: "reasonForUpdate",
      header: "Reason for Update",
      cell: ({ row }) => row.original.reasonForUpdate ?? "",
      minSize: 200,
      enableSorting: false,
    },
    {
      accessorKey: "productsAffected",
      header: "Products Affected",
      cell: ({ row }) => row.original.productsAffectedCount,
      minSize: 140,
      enableSorting: false,
    },
    ...(!isPurchasingCost
      ? ([
          {
            accessorKey: "customers",
            header: "Customers",
            cell: ({ row }) => row.original.customersAffectedCount,
            minSize: 120,
            enableSorting: false,
          },
        ] as ColumnDef<PriceChangeSubmission>[])
      : []),
    {
      accessorKey: "submittedBy",
      header: "Submitted By",
      cell: ({ row }) => row.original?.submittedBy?.name ?? "",
      minSize: 140,
      enableSorting: false,
    },
    ...(!isPurchasingCost
      ? ([
          {
            accessorKey: "assignedToId",
            header: "Assigned To",
            cell: ({ row }) => row.original.assignedTo?.name ?? "",
            minSize: 140,
            enableSorting: false,
          },
        ] as ColumnDef<PriceChangeSubmission>[])
      : []),
    ...(!isPurchasingCost
      ? ([
          {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => <PriceChangeHistoryStatusCell status={row.original.status} />,
            minSize: 130,
            enableSorting: true,
          },
        ] as ColumnDef<PriceChangeSubmission>[])
      : []),

    ...(!isPurchasingCost
      ? ([
          {
            accessorKey: "actions",
            header: "",
            cell: ({ row }) => {
              const { id, isRevertible, status } = row.original

              return (
                <PriceChangeHistoryRowAction
                  onResendToAllCustomers={() => {
                    onResendToAllCustomers?.(row.original)
                  }}
                  onResendToIndividual={(recipientType: RecipientType) => {
                    onResendToIndividual?.(row.original, recipientType)
                  }}
                  status={status}
                  onRevert={() => onRevert?.(id)}
                  isRevertible={isRevertible}
                />
              )
            },
            size: 50,
            enableSorting: false,
          },
        ] as ColumnDef<PriceChangeSubmission>[])
      : []),
  ]
}
